import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

interface ConsentState {
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
}

const defaultConsent: ConsentState = {
  analytics: false,
  advertising: false,
  functional: false,
};

const ConsentContext = createContext<ConsentState>(defaultConsent);

const POLL_INTERVAL_MS = 200;
const POLL_TIMEOUT_MS = 10_000;

function readConsentState(): ConsentState {
  if (typeof window === "undefined") return defaultConsent;
  const state = window.Termly?.getConsentState();
  if (!state) return defaultConsent;

  return {
    analytics: state.analytics,
    advertising: state.advertising,
    functional: state.functional,
  };
}

interface ConsentProviderProps {
  children: ReactNode;
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const [consent, setConsent] = useState<ConsentState>(defaultConsent);

  useEffect(() => {
    if (window.Termly) {
      setConsent(readConsentState());
      window.Termly.on("consent", () => setConsent(readConsentState()));
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (window.Termly) {
        clearInterval(interval);
        setConsent(readConsentState());
        window.Termly.on("consent", () => setConsent(readConsentState()));
      } else if (Date.now() - startTime > POLL_TIMEOUT_MS) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  return <ConsentContext.Provider value={consent}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentState {
  return useContext(ConsentContext);
}
